import React, { useState } from "react";
import { Box, Button, CircularProgress, Grid, TextField, Typography } from "@mui/material";
import { Contact } from "../../../interfaces/DTO/crm/contact";
import { usePartialUpdateContact } from "../../../hooks/crm/useContacts";
import HouseIllustration from "./HouseIllustration";

interface AddressDraft {
  street: string;
  house_number: string;
  city: string;
}

const toDraft = (contact: Contact): AddressDraft => ({
  street: contact.street ?? "",
  house_number: contact.house_number ?? "",
  city: contact.city ?? "",
});

export const formatContactAddress = (a: AddressDraft) =>
  [[a.street, a.house_number].filter(Boolean).join(" "), a.city].filter(Boolean).join(", ");

/** כתובת מגורים – ציור בית והכתובת; "ערוך" מעביר את הכרטיס למצב עריכה (כמו כרטיס הלקוח במגירת הייעוץ) */
const ContactAddressCard: React.FC<{ contact: Contact }> = ({ contact }) => {
  const updateContact = usePartialUpdateContact();
  const [draft, setDraft] = useState<AddressDraft | null>(null);

  const current = toDraft(contact);
  const addressText = formatContactAddress(current);

  const save = async () => {
    if (!draft) return;
    const changed = (Object.keys(draft) as (keyof AddressDraft)[]).filter((k) => draft[k].trim() !== current[k]);
    if (changed.length > 0) {
      await updateContact.mutateAsync({
        id: contact.id!,
        fields: Object.fromEntries(changed.map((k) => [k, draft[k].trim()])) as Partial<Contact>,
      });
    }
    setDraft(null);
  };

  return (
    // the same look as EditableFieldsBlock - the blocks around it
    <Box sx={{ width: "100%", p: 2, borderRadius: 2, boxShadow: 1, backgroundColor: "background.default" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">כתובת מגורים</Typography>
        {!draft && (
          <Button size="small" onClick={() => setDraft(current)}>
            ערוך
          </Button>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <HouseIllustration width={56} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {draft ? (
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={5}>
                <TextField
                  size="small"
                  label="רחוב"
                  value={draft.street}
                  onChange={(e) => setDraft({ ...draft, street: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && save()}
                  fullWidth
                  autoFocus
                />
              </Grid>
              <Grid item xs={4} sm={2}>
                <TextField
                  size="small"
                  label="מס׳"
                  value={draft.house_number}
                  onChange={(e) => setDraft({ ...draft, house_number: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && save()}
                  fullWidth
                />
              </Grid>
              <Grid item xs={8} sm={5}>
                <TextField
                  size="small"
                  label="ישוב"
                  value={draft.city}
                  onChange={(e) => setDraft({ ...draft, city: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && save()}
                  fullWidth
                />
              </Grid>
            </Grid>
          ) : addressText ? (
            <Typography variant="h6" fontWeight={500} sx={{ lineHeight: 1.4 }}>
              {addressText}
            </Typography>
          ) : (
            <Typography color="text.secondary">לא הוזנה כתובת</Typography>
          )}
        </Box>
      </Box>

      {draft && (
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button
            variant="contained"
            onClick={save}
            disabled={updateContact.isPending}
            startIcon={updateContact.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            שמור
          </Button>
          <Button variant="outlined" onClick={() => setDraft(null)} disabled={updateContact.isPending}>
            חזור
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ContactAddressCard;
